import React from "react";
import Marquee from "react-fast-marquee";
import Reveal from "./Reveal";

const eras = [
  {
    dates: "2004 — 2009",
    chapter: "The Forex Era",
    intro:
      "Six ventures stacked around one market — brokerage, tooling, network, media, academy. I learned to build an ecosystem.",
    ventures: [
      { name: "Trading Intl", note: "First introducing brokerage — where it all started." },
      { name: "4X Charts", note: "Customizable-alert charting software. The precursor to ForeXecutor." },
      { name: "ForeXecutor", note: "24/7 auto-executing forex trading platform — alert-driven." },
      { name: "Forex IB", note: "Introducing-broker network for forex brokerages." },
      { name: "Forex Magazine", note: "Our periodical of record for foreign exchange news." },
      { name: "Currency College", note: "Online university teaching people how to trade forex." },
    ],
  },
  {
    dates: "2009 — 2024",
    chapter: "The Marketing Era",
    intro:
      "A 15-year lead-generation engine for Utah small businesses. And a short-lived social arm that taught me everything about social media.",
    ventures: [
      {
        name: "Search Optimizers",
        note: "SEO agency — a lead-gen machine for Utah dentists & small businesses. 15-year run.",
      },
      { name: "Street Social", note: "Social-media-management arm. Short-lived. Learned a lot." },
    ],
  },
  {
    dates: "2010 — today",
    chapter: "With Family",
    intro:
      "The one my wife runs. Her pride and joy — still shipping after 15+ years.",
    ventures: [
      {
        name: "Glitties",
        note:
          "Cosmetic glitter in every color of the rainbow. Amazon + direct. Still active — this one's hers.",
        active: true,
      },
    ],
  },
  {
    dates: "2015 — 2019",
    chapter: "The Amazon Era",
    intro:
      "Before the mint, before STBL — good-tasting nutraceuticals that actually sold.",
    ventures: [
      {
        name: "Gummez",
        note:
          "Good-tasting ashwagandha, apple cider vinegar, elderberry & more. Sold direct on Amazon.",
      },
    ],
  },
  {
    dates: "2017 — 2020",
    chapter: "The Resort Deal",
    intro:
      "A 2% stake in a real resort on the Kamas / Park City border. Three years of real estate education in one of Utah's most iconic addresses.",
    ventures: [
      {
        name: "BlackRock Mountain Resort",
        note:
          "2% ownership in a Park City–area ski & mountain resort. Exited 2020 with a stake in one of Utah's most beautiful addresses.",
      },
    ],
  },
  {
    dates: "2020 — 2023",
    chapter: "The Mint",
    intro:
      "Built it in 2020. Sold $35M in gold & silver in 2023. Then sold the business — and that seed became STBL.",
    highlight: "$35M in gold & silver sold in 2023",
    ventures: [
      {
        name: "The Ohio Mint",
        note: "Minting company. The direct ancestor of STBL.io.",
      },
      {
        name: "TruReviews",
        note:
          "Software that aggregated reviews across Google, Yelp & Facebook into a single 'true' rating per business.",
      },
    ],
  },
];

const marqueeNames = [
  "Trading Intl",
  "4X Charts",
  "ForeXecutor",
  "Forex IB",
  "Forex Magazine",
  "Currency College",
  "Search Optimizers",
  "Street Social",
  "Glitties",
  "Gummez",
  "BlackRock Mountain Resort",
  "The Ohio Mint",
  "TruReviews",
];

export const PastPortfolio = () => {
  return (
    <section
      id="portfolio"
      data-testid="past-portfolio-section"
      className="relative py-28 lg:py-40 border-t border-gold-deep/40 z-10 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 mb-16 lg:mb-20">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <div className="overline mb-6">Past · The Receipts</div>
              <h2 className="heading-section text-5xl lg:text-7xl text-white max-w-2xl">
                Six eras. Thirteen <span className="italic gold-gradient-text">chapters.</span>
              </h2>
            </div>
            <p className="body-copy max-w-md text-base lg:text-right">
              Mints, magazines, marketing firms, marketplaces, and more. Every venture left a
              lesson that compounds.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Marquee accent */}
      <div className="relative mb-20">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-48 bg-gradient-to-r from-black to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-48 bg-gradient-to-l from-black to-transparent z-10" />
        <Marquee gradient={false} speed={40} pauseOnHover>
          {marqueeNames.map((name) => (
            <div
              key={name}
              className="ticker-item"
              data-testid={`past-item-${name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            >
              <span>{name}</span>
              <span className="ticker-dot" />
            </div>
          ))}
        </Marquee>
      </div>

      {/* Era timeline */}
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="relative">
          {/* Vertical line */}
          <div
            className="hidden lg:block absolute left-[220px] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold-deep to-transparent"
            aria-hidden="true"
          />

          <div className="space-y-20 lg:space-y-28">
            {eras.map((era, idx) => (
              <Reveal
                key={era.chapter}
                delay={idx * 80}
                className="relative grid lg:grid-cols-[220px_1fr] gap-10 lg:gap-16"
                data-testid={`era-${era.chapter.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              >
                {/* Era label */}
                <div className="lg:sticky lg:top-24 lg:self-start">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80 mb-3">
                    Chapter {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="heading-section text-3xl lg:text-4xl text-white leading-none mb-2">
                    {era.dates}
                  </div>
                  <div className="font-serif italic text-gold text-xl lg:text-2xl">
                    {era.chapter}
                  </div>
                </div>

                {/* Bullet on timeline */}
                <span
                  className="hidden lg:block absolute left-[214px] top-2 w-3 h-3 rounded-full bg-gold ring-4 ring-black"
                  aria-hidden="true"
                />

                {/* Era content */}
                <div className="lg:pl-6">
                  <p className="body-copy text-base lg:text-lg text-white/60 max-w-2xl mb-8">
                    {era.intro}
                  </p>

                  {era.highlight && (
                    <div
                      className="inline-flex items-center gap-3 mb-8 px-5 py-3 border border-gold/50 bg-[#12100a]"
                      data-testid={`era-highlight-${idx}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                      <span className="font-serif text-gold text-xl lg:text-2xl">
                        {era.highlight}
                      </span>
                    </div>
                  )}

                  <div className="grid gap-px bg-gold-deep/20 border border-gold-deep/30">
                    {era.ventures.map((v) => (
                      <div
                        key={v.name}
                        className="group p-6 lg:p-7 bg-[#070707] hover:bg-[#0c0a06] transition-colors duration-500"
                        data-testid={`era-venture-${v.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      >
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h3 className="heading-section text-2xl lg:text-3xl text-white group-hover:text-gold-light transition-colors">
                                {v.name}
                              </h3>
                              {v.active && (
                                <span className="inline-flex items-center gap-2 px-2.5 py-1 border border-gold/60 font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                                  <span className="w-1 h-1 rounded-full bg-gold animate-pulse" />
                                  Active
                                </span>
                              )}
                            </div>
                            <p className="body-copy text-sm lg:text-base max-w-2xl">{v.note}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        <Reveal delay={200}>
          <div className="mt-24 pt-10 border-t border-gold-deep/40 flex flex-wrap items-center justify-between gap-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-16">
              <div>
                <div className="font-serif text-gold text-4xl lg:text-5xl">13</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-2">
                  Ventures Shipped
                </div>
              </div>
              <div>
                <div className="font-serif text-gold text-4xl lg:text-5xl">20+</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-2">
                  Years Building
                </div>
              </div>
              <div>
                <div className="font-serif text-gold text-4xl lg:text-5xl">$35M</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-2">
                  Peak Year · Ohio Mint
                </div>
              </div>
              <div>
                <div className="font-serif text-gold text-4xl lg:text-5xl">∞</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-2">
                  Lessons Taken
                </div>
              </div>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 max-w-xs">
              Some sold. Some learned. All shaped what came next.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default PastPortfolio;
